import { useState } from 'react';
import { AlertTriangle, Truck } from 'lucide-react';
import { teamHeadcount, validateTaskTeams } from '../../../core/assignment-rules';
import type { ScheduleEntry, TaskWindow } from '../../../core/conflict-checker';
import type { TaskTeam } from '../../../types/assignment';
import type { Soldier, Vehicle } from '../../../types/fleet';
import type { User } from '../../../types/user';
import { AddTeamButton, AddTeamModal } from './AddTeamModal';
import { TaskTeamCard } from './TaskTeamCard';

interface TaskTeamsSectionProps {
  /** Empty while the task is still being created — the store stamps the real id. */
  taskId: string;
  teams: readonly TaskTeam[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /** The task's own window, so the form can check availability in time. */
  task?: TaskWindow | null;
  /** Every other open task with a convoy, for the time-overlap check. */
  schedule?: readonly ScheduleEntry[];
  /** The `User.id` that signs a "שבץ בכל זאת". */
  managerId?: string;
  /** The operator, so an איש חוץ can be kept for next time. */
  user?: User | null;
  /** `ASSIGN_TASK`. A viewer without it sees the convoy but cannot change it. */
  canManage: boolean;
  onSaveTeam: (team: TaskTeam) => void | Promise<void>;
  onRemoveTeam: (team: TaskTeam) => void | Promise<void>;
}

/** Which team the modal is open for: a new vehicle, or one already on the task. */
type TeamTarget = { mode: 'add' } | { mode: 'edit'; team: TaskTeam };

/**
 * The convoy of one task: a card per vehicle team and the button that adds the
 * next one. Both hosts — the task form and the board's team dialog — render
 * this, so a crew looks and behaves the same wherever it is edited.
 */
export function TaskTeamsSection({
  taskId,
  teams,
  vehicles,
  soldiers,
  task,
  schedule,
  managerId,
  user,
  canManage,
  onSaveTeam,
  onRemoveTeam,
}: TaskTeamsSectionProps) {
  const [target, setTarget] = useState<TeamTarget | null>(null);

  const riders = teams.reduce((total, team) => total + teamHeadcount(team), 0);
  const conflicts = validateTaskTeams(teams);

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between gap-2">
        <p className="text-sm text-ink/60 flex items-center gap-2">
          <Truck size={16} className="text-ink/40" aria-hidden />
          <span>
            {teams.length} רכבים · {riders} אנשי צוות
          </span>
        </p>
        {canManage ? <AddTeamButton onClick={() => setTarget({ mode: 'add' })} /> : null}
      </header>

      {teams.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line p-4 text-center text-sm text-ink/50">
          טרם שובצו רכבים למשימה. כל נהג, מפקד משימה ונוסע משויך לרכב שאליו הוא עולה.
        </p>
      ) : (
        <div className="space-y-3">
          {teams.map((team) => (
            <TaskTeamCard
              key={team.id}
              team={team}
              vehicles={vehicles}
              soldiers={soldiers}
              onEdit={canManage ? (current) => setTarget({ mode: 'edit', team: current }) : undefined}
              onRemove={canManage ? (current) => void onRemoveTeam(current) : undefined}
            />
          ))}
        </div>
      )}

      {conflicts.length > 0 ? (
        <ul className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 space-y-1">
          {conflicts.map((conflict) => (
            <li key={`${conflict.teamId}-${conflict.code}`} className="flex items-start gap-1.5">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
              <span>{conflict.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {target ? (
        <AddTeamModal
          taskId={taskId}
          teams={teams}
          team={target.mode === 'edit' ? target.team : null}
          vehicles={vehicles}
          soldiers={soldiers}
          task={task}
          schedule={schedule}
          managerId={managerId}
          user={user}
          onClose={() => setTarget(null)}
          onSave={onSaveTeam}
        />
      ) : null}
    </div>
  );
}
